import { DOCUMENT, isPlatformBrowser } from "@angular/common";
import { Inject, Injectable, OnDestroy, PLATFORM_ID } from "@angular/core";

/**
 * Proveedores que avisan de su alto real por `postMessage`.
 *
 * `origen` tiene que coincidir **exacto** con el `event.origin` del mensaje: es la única
 * comprobación que impide que cualquier otro iframe de la página nos redimensione embeds.
 * `clase` es la que `crearIframe()` pone en el iframe de ese proveedor
 * (`DOMINIOS_EMBED_PERMITIDOS`), y acota la búsqueda del marco al que aplicar la medida.
 */
const PROVEEDORES_MEDIDA: readonly { origen: string; clase: string }[] = [{ origen: "https://www.instagram.com", clase: "embed-rrss-instagram" }];

/**
 * Límites del alto que se acepta de un mensaje. El filtro por origen ya deja fuera a terceros;
 * esto solo evita que un valor absurdo (0, negativo, enorme) destroce la maquetación.
 */
const ALTO_MINIMO = 100;
const ALTO_MAXIMO = 5000;

@Injectable({
  providedIn: "root",
})
/**
 * Ajusta el alto de los embeds que no pueden crecer solos.
 *
 * El alto que necesita una publicación de Instagram depende del ancho al que se pinte y un
 * iframe no crece con su contenido. La página `/embed` de Instagram publica su altura con un
 * `postMessage` de tipo `MEASURE`; aquí se escucha y se aplica al iframe que lo mandó.
 *
 * - **Un único listener para toda la aplicación** (se engancha en `app.config.ts`), no uno por
 *   noticia: el mensaje puede llegar en cualquier momento —al cargar, al redimensionar y, con
 *   el bloqueo de consentimiento, solo cuando la directiva crea el iframe— y un listener por
 *   componente se acumularía en cada navegación de la SPA.
 * - El marco se identifica comparando `contentWindow` con el `source` del evento, **nunca por
 *   índice** ni por un id que venga en el mensaje: así da igual el orden y cuántos embeds haya.
 *
 * Solo hace algo en navegador; en SSR no hay nada que medir.
 */
export class AltoEmbedsService implements OnDestroy {
  private escuchando = false;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    @Inject(DOCUMENT) private documento: Document,
  ) {}

  /** Engancha el escuchador. Idempotente: llamarlo más de una vez no duplica nada. */
  escuchar(): void {
    if (this.escuchando || !isPlatformBrowser(this.platformId)) {
      return;
    }
    const ventana = this.documento.defaultView;
    if (!ventana) {
      return;
    }
    this.escuchando = true;
    ventana.addEventListener("message", this.alMensaje, false);
  }

  ngOnDestroy(): void {
    if (!this.escuchando) {
      return;
    }
    this.escuchando = false;
    this.documento.defaultView?.removeEventListener("message", this.alMensaje, false);
  }

  /**
   * Campo de instancia y no método para que `this` siga siendo el servicio cuando lo llama el
   * navegador, y para poder quitar el mismo listener que se puso.
   */
  private alMensaje = (evento: MessageEvent): void => {
    const proveedor = PROVEEDORES_MEDIDA.find((p) => p.origen === evento.origin);
    if (!proveedor) {
      return;
    }
    const alto = this.altoMedido(evento.data);
    if (alto === null) {
      return;
    }
    const marco = this.marcoDelMensaje(proveedor.clase, evento.source);
    if (marco) {
      marco.style.height = `${alto}px`;
    }
  };

  /** Saca el alto de un mensaje `MEASURE`, o `null` si el mensaje no es eso o no es razonable. */
  private altoMedido(datos: unknown): number | null {
    // Instagram lo manda como cadena JSON; cualquier otra forma no es un mensaje suyo.
    if (typeof datos !== "string") {
      return null;
    }
    let mensaje: any;
    try {
      mensaje = JSON.parse(datos);
    } catch {
      return null;
    }
    if (mensaje?.type !== "MEASURE") {
      return null;
    }
    const alto = Number(mensaje?.details?.height);
    return Number.isFinite(alto) && alto >= ALTO_MINIMO && alto <= ALTO_MAXIMO ? Math.round(alto) : null;
  }

  /** El iframe del proveedor cuya ventana ha mandado el mensaje. */
  private marcoDelMensaje(clase: string, fuente: MessageEventSource | null): HTMLIFrameElement | null {
    if (!fuente) {
      return null;
    }
    for (const marco of Array.from(this.documento.querySelectorAll<HTMLIFrameElement>(`iframe.${clase}`))) {
      if (marco.contentWindow === fuente) {
        return marco;
      }
    }
    return null;
  }
}
